import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import Preferences from 'Preferences';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {DatasetFileStateQuery, DatasetFileStateQueryVariables} from './__generated/DatasetFileStateQuery';
import DatasetTabState from './DatasetTabState';

type DatasetNodeGraphql = ExtractByTypename<DatasetFileStateQuery['dataset'], 'DatasetNode'>;

export const DATASET_FILE_STATE_QUERY: DocumentNode = gql`
  query DatasetFileStateQuery($fileId: StrictString!) @skipAudit @engComponent(name: DATA_FRAMES) {
    dataset(id: $fileId) {
      ... on DatasetNode {
        ...BaseFolderItemFileGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class Dataset extends ApolloFolderItemBase<
  DatasetFileStateQuery,
  DatasetNodeGraphql,
  DatasetFileStateQueryVariables
> {
  protected getQueryWrapper(): QueryWrapper<DatasetFileStateQuery, DatasetFileStateQueryVariables> {
    return {query: DATASET_FILE_STATE_QUERY};
  }

  protected queryVariables(): DatasetFileStateQueryVariables {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: DatasetFileStateQuery
  ): DatasetNodeGraphql | InaccessibleInfoGraphQL {
    return data.dataset;
  }

  protected getUpdatedQueryData(attrs: Partial<DatasetNodeGraphql>): DatasetFileStateQuery {
    return {
      dataset: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }
}

interface DatasetFileState extends FolderItemState {
  model(): Dataset;
}

class DatasetFileState extends FolderItemState {
  static override Model = Dataset;
  static override fileType = ModelTypeConstants.DATASET;

  static override initClass(): void {
    this.prototype.hasNote = false;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  override initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);
    const datasetTab = new DatasetTabState({id: this.id});
    this.buffers().at(0).tabs().add(datasetTab);
    datasetTab.set({active: true});
    const splitPref = Preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : false);
  }
}

DatasetFileState.initClass();

export default DatasetFileState;
